import React, { useContext, useState } from 'react';
import { Box, Typography, Grid, CircularProgress, Tab, useTheme } from '@mui/material';
import { Screen } from '../../components';
import { makeStyles } from '@mui/styles';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { UserContext } from '../../lib/contexts';
import { NutrientsContextProvider, NutrientsContext } from '../../lib/contexts/NutrientsContext';
import AssessDietButton from '../../components/AssessDietButton';
import NutrientProfile from '../../components/NutrientProfile';
import BioBox from '../../components/BioBox';
import DietBox from '../../components/DietBox';
import useDiet from '../../lib/hooks/useDiet';
import { getWorkflowToken } from '../../lib/helpers/helpers';
import usePatientNutrients from '../../lib/hooks/usePatientNutrients';
import NutrientsBox from '../../components/NutrientsBox';
import NutrientChanges from '../../components/NutrientChanges';
import FoodGroupsBox from '../../components/FoodGroupsBox';
import { getNutrientChangeData } from '../../lib/helpers/nutrientFormatHelpers';

const LoadingComponent = ({ height = '300px' }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: height,
        width: '100%',
      }}
    >
      <CircularProgress />
    </Box>
  );
};

const FoodGroupsTab = () => {
  /**
   * Get food_changes data from useMetric hook instead of nutrients context
   * const { data: metricsData, isLoading: metricsLoading } = useMetric('priorities', userId);
   * only works for providers; using the data in workflow_share which doesn't have the food_changes data
   */
  const { state, isLoading } = useContext(NutrientsContext);
  const increases = getNutrientChangeData(state?.allNutrientsData?.to_increase, true);
  const decreases = getNutrientChangeData(state?.allNutrientsData?.to_decrease, false);

  return (
    <Box sx={{ pb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }, // Column on mobile, row on desktop
          gap: '24px',
          mt: 2,
          flexWrap: { xs: 'nowrap', md: 'wrap' }, // Allow wrapping on smaller screens if needed
        }}
      >
        {!isLoading && increases && increases.length > 0 && (
          <>
            <NutrientChanges
              data={increases}
              isIncrease={true}
              heading={'Increase These'}
              tip={'Start introducing more of these in your diet to improve your condition.'}
              maxWidth={'336'}
            />
            {decreases?.length > 0 && (
              <NutrientChanges
                data={decreases}
                isIncrease={false}
                heading="Decrease These"
                tip="These food groups should be decreased in your diet"
                maxWidth={'336'}
              />
            )}
          </>
        )}
        <FoodGroupsBox />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  screen: {
    // update so screen doesn't jump to center when tabs switch
    marginLeft: '38px',
  },
  tabPanel: {
    margin: 0,
    padding: 0,
    width: '100%',
  },
  tabRoot: {
    color: theme.palette.admin.dark,
    borderColor: theme.palette.admin.dark,
  },
  TabTextColor: {
    color: theme.palette.admin.main,
    borderColor: theme.palette.admin.main,
  },
}));

export const DietQualityScreen = () => {
  const classes = useStyles();
  const theme = useTheme();
  const {
    state: { userObject },
  } = useContext(UserContext);

  const [currentTab, setCurrentTab] = useState('0');

  const userId = userObject?.user_id || userObject?.id;

  // get workflow token so we can get the nutrient data
  const workflowToken = getWorkflowToken(userObject?.workflowShareUrl);
  const { isLoading: nutrientsDataLoading, data: nutrientsData } = usePatientNutrients(workflowToken);

  const assessmentData = userObject?.lastWorkflow;

  const { isLoading: currentDietLoading, data: currentDiet } = useDiet(
    userObject?.lastType,
    userObject?.lastQuality,
    userId,
  );

  const { isLoading: goalDietLoading, data: goalDiet } = useDiet(userObject?.goalType, userObject?.goalQuality, userId);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const a11yProps = (index) => {
    return {
      value: String(index),
      id: `scrollable-tab-${index}`,
      'aria-controls': `scrollable-tabpanel-${index}`,
    };
  };

  // This function determines the max width based on current tab
  const getMaxWidth = () => {
    return currentTab === '1' ? '1056px' : '1242px';
  };

  return (
    <Screen
      title={'Diet Quality - Diet ID'}
      showSidebarNav={true}
      isAdmin={false}
    >
      <Grid
        container
        alignItems="center"
        spacing={0}
        className={classes.screen}
        sx={{ maxWidth: getMaxWidth() }}
      >
        <Box sx={{ width: '100%', padding: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'center', sm: 'space-between' },
              alignItems: 'center',
              marginBottom: { xs: '24px', sm: '32px' },
              gap: { xs: '24px', sm: 0 },
              padding: 0,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                textAlign: { xs: 'center', sm: 'left' },
              }}
            >
              Your Diet Quality
            </Typography>
            <AssessDietButton />
          </Box>

          <TabContext value={currentTab}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'flex-start' },
                marginBottom: { xs: '24px', sm: '32px' },
                padding: 0,
              }}
            >
              <TabList
                value={currentTab}
                onChange={handleTabChange}
                indicatorColor="secondary"
                textColor="secondary"
                aria-label="diet quality tabs"
                variant="scrollable"
                visibleScrollbar={true}
                scrollButtons={false}
                sx={{
                  width: { xs: 'auto', sm: '100%' },
                  maxWidth: '100%',
                  padding: 0,
                  '& .MuiTab-root': {
                    color: 'rgba(0, 0, 0, 0.60)',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '24px',
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                    minWidth: '100px', // Keep this as minimum
                    padding: '12px 16px',
                    textAlign: 'center',
                    boxSizing: 'border-box',
                  },
                  '& .MuiTab-root:nth-of-type(2)': {
                    // Target specifically the Food Groups tab
                    width: '148px',
                    minWidth: '148px',
                  },
                  '& .MuiTab-root:not(:last-child)': {
                    marginRight: '16px',
                  },
                  '& .Mui-selected': {
                    color: `${theme.palette.admin.main} !important`,
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: `${theme.palette.admin.main}`,
                  },
                }}
              >
                <Tab
                  label="Results"
                  {...a11yProps(0)}
                />
                <Tab
                  label="Food Groups"
                  {...a11yProps(1)}
                />
                <Tab
                  label="Nutrients"
                  {...a11yProps(2)}
                />
              </TabList>
            </Box>

            <TabPanel
              value="0"
              className={classes.tabPanel}
            >
              {currentDietLoading || goalDietLoading ? (
                <LoadingComponent />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    width: '100%',
                    padding: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: '24px', sm: 0 },
                      width: '100%',
                      padding: 0,
                      justifyContent: 'space-between',
                      position: 'relative',
                      flexWrap: { sm: 'wrap', lg: 'nowrap' },
                      alignItems: 'flex-start',
                    }}
                  >
                    {/* BioBox column */}
                    {!currentDietLoading &&
                      !goalDietLoading &&
                      currentDiet?.data?.name &&
                      currentDiet?.data?.quality && (
                        <Box
                          sx={{
                            width: { xs: '100%', sm: '360px' },
                            flexShrink: 0,
                            flexGrow: 0,
                            marginRight: { xs: 0, sm: '24px' },
                            boxSizing: 'border-box',
                          }}
                        >
                          <BioBox
                            bioData={{
                              dietName: currentDiet?.data?.name,
                              dietQuality: currentDiet?.data?.quality,
                              idealName: goalDiet?.data?.name,
                              idealQuality: goalDiet?.data?.quality,
                              ...userObject,
                            }}
                            assessmentData={assessmentData}
                            checkinHistoryData={userObject?.lastCheckinAt}
                          />
                        </Box>
                      )}

                    {/* DietBoxes column - with fixed width to match the nutrients tab */}
                    <Box
                      sx={{
                        width: { xs: '100%', sm: '100%', lg: 'calc(100% - 384px)' },
                        flexShrink: 1,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                        marginTop: { sm: '24px', lg: 0 },
                        boxSizing: 'border-box',
                      }}
                    >
                      {userObject?.lastType && userObject?.lastQuality && (
                        <DietBox
                          title="Your Current Diet"
                          type={userObject?.lastType}
                          quality={userObject?.lastQuality}
                        />
                      )}

                      {userObject?.goalType && userObject?.goalQuality && (
                        <DietBox
                          title="Your Goal Diet"
                          type={userObject?.goalType}
                          quality={userObject?.goalQuality}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </TabPanel>

            <TabPanel
              value="1"
              className={classes.tabPanel}
            >
              {nutrientsDataLoading || !workflowToken ? (
                <LoadingComponent />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: '24px',
                    width: '100%',
                    padding: 0,
                  }}
                >
                  {workflowToken && !nutrientsDataLoading && nutrientsData && (
                    <NutrientsContextProvider workflowToken={workflowToken}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '24px',
                          flex: 1,
                        }}
                      >
                        <FoodGroupsTab />
                      </Box>
                    </NutrientsContextProvider>
                  )}
                </Box>
              )}
            </TabPanel>

            <TabPanel
              value="2"
              className={classes.tabPanel}
            >
              {nutrientsDataLoading || !workflowToken ? (
                <LoadingComponent />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: '24px',
                    width: '100%',
                    padding: 0,
                  }}
                >
                  {workflowToken && !nutrientsDataLoading && nutrientsData && (
                    <NutrientsContextProvider workflowToken={workflowToken}>
                      <Box sx={{ width: { xs: '100%', sm: '336px' }, flexShrink: 0 }}>
                        <NutrientProfile data={nutrientsData} />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '24px',
                          flex: 1,
                        }}
                      >
                        <NutrientsBox type="macronutrients" />
                        <NutrientsBox type="nutrients" />
                        <NutrientsBox type="fats" />
                        <NutrientsBox type="minerals" />
                        <NutrientsBox type="vitamins" />
                      </Box>
                    </NutrientsContextProvider>
                  )}
                </Box>
              )}
            </TabPanel>
          </TabContext>
        </Box>
      </Grid>
    </Screen>
  );
};

export default DietQualityScreen;
